import React from 'react';

// Exact path from public/assets/logos/logo.svg
const DAKHLA_SINGLE_PATH = "M59.01,2.53 C59.01,2.53 852.92,19.19 852.92,492.51 C852.92,1054.90 2.14,983.61 2.14,983.61 L19.20,622.47 L277.98,503.03 L22.04,375.06 Z";

/**
 * DakhlaButterflyIcon
 * Formed by joining the Dakhla logo with its horizontally flipped counterpart at their rounded apexes,
 * recreating the exact butterfly emblem with realistic aerodynamics & physics.
 */
export const DakhlaButterflyIcon = ({ 
  className = "w-14 h-8", 
  animated = false,
  color = "#393FEE", // Signature Dakhla Royal Blue
  secondaryColor = "#4F46E5"
}) => {
  return (
    <svg
      className={`inline-block ${className}`}
      viewBox="-950 -550 1900 1100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="dakhla-butterfly-left-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={secondaryColor} />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
        <linearGradient id="dakhla-butterfly-right-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={color} />
          <stop offset="100%" stopColor={secondaryColor} />
        </linearGradient>
      </defs>

      <g className={animated ? "animate-butterfly-body" : ""}>
        {/* Left Wing - Translated so rounded apex touches center (0,0) */}
        <g className={animated ? "animate-wing-flap-left origin-[0px_0px]" : ""}>
          <g transform="translate(-852.92, -492.51)">
            <path
              d={DAKHLA_SINGLE_PATH}
              fill="url(#dakhla-butterfly-left-grad)"
            />
          </g>
        </g>

        {/* Right Wing - Mirrored horizontally so rounded apex touches center (0,0) */}
        <g className={animated ? "animate-wing-flap-right origin-[0px_0px]" : ""}>
          <g transform="scale(-1, 1) translate(-852.92, -492.51)">
            <path
              d={DAKHLA_SINGLE_PATH}
              fill="url(#dakhla-butterfly-right-grad)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

/**
 * DakhlaButterflyLoader
 * Smooth, physics-based loading animation with realistic butterfly flight dynamics.
 * Features asymmetric power/recovery strokes, aerodynamic wing flexing, and synchronized body lift.
 */
const DakhlaButterflyLoader = ({ size = "md", text = "Loading...", fullScreen = false, className = "" }) => {
  const sizeClasses = {
    sm: "w-10 h-6",
    md: "w-14 h-8",
    lg: "w-20 h-12"
  };

  const containerClasses = fullScreen
    ? "fixed inset-0 bg-slate-100 z-40 flex flex-col items-center justify-center p-6 space-y-3 select-none"
    : `w-full min-h-[350px] flex flex-col items-center justify-center p-6 space-y-3 select-none ${className}`;

  return (
    <div className={containerClasses}>
      <style>{`
        /* Continuous harmonic wing flap: smooth, fluid, zero-pause flapping */
        @keyframes butterflyFlapLeft {
          0%, 100% {
            transform: rotateY(0deg) rotateZ(0deg) rotateX(0deg) scale(1, 1);
          }
          25% {
            transform: rotateY(44deg) rotateZ(-4deg) rotateX(6deg) scale(0.70, 1.02);
          }
          50% {
            /* Full upward fold / squish */
            transform: rotateY(74deg) rotateZ(-7deg) rotateX(10deg) scale(0.38, 1.05);
          }
          75% {
            transform: rotateY(36deg) rotateZ(-3deg) rotateX(4deg) scale(0.78, 0.99);
          }
        }

        @keyframes butterflyFlapRight {
          0%, 100% {
            transform: rotateY(0deg) rotateZ(0deg) rotateX(0deg) scale(1, 1);
          }
          25% {
            transform: rotateY(-44deg) rotateZ(4deg) rotateX(6deg) scale(0.70, 1.02);
          }
          50% {
            /* Full upward fold / squish */
            transform: rotateY(-74deg) rotateZ(7deg) rotateX(10deg) scale(0.38, 1.05);
          }
          75% {
            transform: rotateY(-36deg) rotateZ(3deg) rotateX(4deg) scale(0.78, 0.99);
          }
        }

        /* Body Dynamics: Smooth lift on thrust, soft dip on fold */
        @keyframes butterflyBodyPhysics {
          0%, 100% {
            transform: translateY(-5px) rotateX(-2deg) scale(1.02);
          }
          50% {
            transform: translateY(3px) rotateX(4deg) scale(0.97);
          }
        }

        /* Ambient Float: Gentle 3D air drift */
        @keyframes butterflyAirDrift {
          0%, 100% {
            transform: translate(0px, 0px) rotate(0deg);
          }
          33% {
            transform: translate(2px, -3px) rotate(1.5deg);
          }
          66% {
            transform: translate(-2px, 2px) rotate(-1.5deg);
          }
        }

        /* Pulse glow synced with flap cycle */
        @keyframes butterflyGlowPulse {
          0%, 100% {
            opacity: 0.35;
            transform: scale(1.15);
          }
          50% {
            opacity: 0.12;
            transform: scale(0.88);
          }
        }

        .animate-wing-flap-left {
          animation: butterflyFlapLeft 0.65s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        .animate-wing-flap-right {
          animation: butterflyFlapRight 0.65s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        .animate-butterfly-body {
          animation: butterflyBodyPhysics 0.65s ease-in-out infinite;
          transform-style: preserve-3d;
        }
        .animate-air-drift {
          animation: butterflyAirDrift 3.4s ease-in-out infinite;
        }
        .animate-glow-pulse {
          animation: butterflyGlowPulse 0.65s ease-in-out infinite;
        }
      `}</style>

      <div className="relative flex items-center justify-center perspective-[500px] animate-air-drift">
        {/* Soft glowing radial background synced with stroke thrust */}
        <div className="absolute w-12 h-12 bg-primary-500/25 rounded-full blur-xl animate-glow-pulse" />
        
        {/* Butterfly Icon */}
        <div className="relative z-10">
          <DakhlaButterflyIcon className={sizeClasses[size] || sizeClasses.md} animated={true} />
        </div>
      </div>

      {text && (
        <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase animate-pulse mt-1">
          {text}
        </p>
      )}
    </div>
  );
};

export default DakhlaButterflyLoader;
